/**
 * Profile page — view + edit personal details (name, phone, company, address,
 * profile photo). Email lives in a separate section because it requires a
 * password re-confirm for email-auth accounts.
 *
 * The page is intentionally split into small, independently-savable cards so
 * one broken field never blocks another — buyers on a shaky mobile connection
 * can save just their phone and move on.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Trash2, User, Mail, Phone, Building2, MapPin, Save, Settings, ShieldCheck } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-md text-sm border focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)] focus:border-transparent";

export const Profile = () => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", phone: "", company: "", address: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Email change is a separate card because it needs password re-confirm.
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailSaving, setEmailSaving] = useState(false);
  const isEmailAuth = user?.auth_provider === "email";

  const fileInputRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      company: user.company || "",
      address: user.address || "",
    });
    setEmailForm({ new_email: "", current_password: "" });
    setDirty(false);
  }, [user, loading, navigate]);

  const onField = (k) => (e) => {
    setForm((s) => ({ ...s, [k]: e.target.value }));
    setDirty(true);
  };

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/profile", form);
      toast.success("Profile updated");
      setDirty(false);
      checkAuth && checkAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const onChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email) return;
    setEmailSaving(true);
    try {
      await api.post("/profile/email", emailForm);
      toast.success("Email updated");
      setEmailForm({ new_email: "", current_password: "" });
      checkAuth && checkAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update email");
    } finally {
      setEmailSaving(false);
    }
  };

  const onPickAvatar = () => fileInputRef.current?.click();

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-selecting the same file next time
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image is too large (max 2 MB)"); return; }
    setAvatarBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post("/profile/avatar", { data_url: dataUrl });
      toast.success("Photo updated");
      checkAuth && checkAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async () => {
    if (!window.confirm("Remove profile photo?")) return;
    setAvatarBusy(true);
    try {
      await api.delete("/profile/avatar");
      toast.success("Photo removed");
      checkAuth && checkAuth();
    } catch {
      toast.error("Could not remove photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  if (loading || !user) return (
    <div className="App"><Header /><div className="max-w-[900px] mx-auto px-6 py-10">Loading…</div><Footer /></div>
  );

  return (
    <div className="App">
      <Header />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="overline mb-2">Account</div>
            <h1 className="font-display text-3xl md:text-4xl">My profile</h1>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
              Details we use on your reports, invoices and lender-ready exports.
            </p>
          </div>
          <button
            onClick={() => navigate("/profile/settings")}
            className="btn-secondary text-sm"
            data-testid="btn-open-settings"
          >
            <Settings size={13} /> Profile settings
          </button>
        </div>

        {/* Photo + identity card */}
        <section className="card p-6 mb-6" data-testid="card-avatar">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="relative">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-24 h-24 rounded-full object-cover"
                  style={{ border: "1px solid var(--hairline)" }}
                  data-testid="avatar-img"
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center font-display text-3xl"
                  style={{ background: "var(--paper)", color: "var(--brand-green)" }}
                  data-testid="avatar-fallback"
                >
                  {(user.name || user.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onAvatarFile}
                data-testid="avatar-file-input"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-lg" data-testid="profile-name">{user.name || "Unnamed"}</div>
              <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="profile-email">{user.email}</div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button
                  onClick={onPickAvatar}
                  disabled={avatarBusy}
                  className="btn-secondary !py-1.5 !px-3 text-xs"
                  data-testid="btn-upload-avatar"
                >
                  <Camera size={12} /> {user.picture ? "Change photo" : "Upload photo"}
                </button>
                {user.picture && (
                  <button
                    onClick={onRemoveAvatar}
                    disabled={avatarBusy}
                    className="text-xs px-3 py-1.5 rounded hover:bg-[var(--paper)] inline-flex items-center gap-1.5"
                    style={{ color: "var(--muted)" }}
                    data-testid="btn-remove-avatar"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                JPG or PNG, up to 2 MB. We resize to 256×256 to keep pages fast.
              </p>
            </div>
          </div>
        </section>

        {/* Personal info card */}
        <form onSubmit={onSaveProfile} className="card p-6 mb-6" data-testid="card-info">
          <div className="flex items-center gap-2 mb-4">
            <User size={16} style={{ color: "var(--brand-green)" }} />
            <h2 className="font-display text-lg">Personal details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" icon={User}>
              <input
                className={inputCls}
                style={{ borderColor: "var(--hairline)" }}
                value={form.name}
                onChange={onField("name")}
                placeholder="Your full name"
                data-testid="input-name"
              />
            </Field>
            <Field label="Mobile number" icon={Phone}>
              <input
                className={inputCls}
                style={{ borderColor: "var(--hairline)" }}
                value={form.phone}
                onChange={onField("phone")}
                placeholder="+27 …"
                data-testid="input-phone"
              />
            </Field>
            <Field label="Company" icon={Building2}>
              <input
                className={inputCls}
                style={{ borderColor: "var(--hairline)" }}
                value={form.company}
                onChange={onField("company")}
                placeholder="Company name"
                data-testid="input-company"
              />
            </Field>
            <Field label="Address" icon={MapPin} full>
              <textarea
                className={inputCls}
                style={{ borderColor: "var(--hairline)", minHeight: "70px", resize: "vertical" }}
                value={form.address}
                onChange={onField("address")}
                placeholder="Street, city, country"
                data-testid="input-address"
              />
            </Field>
          </div>
          <div className="flex justify-end mt-5">
            <button
              type="submit"
              disabled={!dirty || saving}
              className="btn-primary text-sm"
              data-testid="btn-save-profile"
            >
              <Save size={13} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>

        {/* Email change card */}
        <form onSubmit={onChangeEmail} className="card p-6 mb-6" data-testid="card-email">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={16} style={{ color: "var(--brand-green)" }} />
            <h2 className="font-display text-lg">Email address</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
            Your current email is <span className="font-mono" style={{ color: "var(--charcoal)" }}>{user.email}</span>.
            {isEmailAuth ? " Enter your current password to confirm." : " Google sign-in accounts do not need a password to change email."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="New email">
              <input
                type="email"
                className={inputCls}
                style={{ borderColor: "var(--hairline)" }}
                value={emailForm.new_email}
                onChange={(e) => setEmailForm((s) => ({ ...s, new_email: e.target.value }))}
                placeholder="you@company.com"
                data-testid="input-new-email"
                required
              />
            </Field>
            {isEmailAuth && (
              <Field label="Current password">
                <input
                  type="password"
                  className={inputCls}
                  style={{ borderColor: "var(--hairline)" }}
                  value={emailForm.current_password}
                  onChange={(e) => setEmailForm((s) => ({ ...s, current_password: e.target.value }))}
                  placeholder="Password"
                  data-testid="input-email-password"
                  required
                />
              </Field>
            )}
          </div>
          <div className="flex justify-end mt-5">
            <button
              type="submit"
              disabled={!emailForm.new_email || emailSaving || (isEmailAuth && !emailForm.current_password)}
              className="btn-primary text-sm"
              data-testid="btn-save-email"
            >
              <Save size={13} /> {emailSaving ? "Updating…" : "Update email"}
            </button>
          </div>
        </form>

        {/* Deep-link to settings for password + notifications + delete */}
        <section className="card p-5 mb-10 flex items-center justify-between flex-wrap gap-3" style={{ background: "var(--paper)" }}>
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} style={{ color: "var(--brand-green)" }} />
            <div>
              <div className="font-display text-base" style={{ color: "var(--charcoal)" }}>Password, notifications & account controls</div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>Change password, choose which emails you receive, or delete your account.</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/profile/settings")}
            className="btn-secondary text-sm"
            data-testid="btn-jump-settings"
          >
            Open profile settings →
          </button>
        </section>
      </div>
      <Footer />
    </div>
  );
};

const Field = ({ label, icon: Icon, children, full }) => (
  <label className={"block " + (full ? "md:col-span-2" : "")}>
    <span className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
      {Icon && <Icon size={11} />} {label}
    </span>
    {children}
  </label>
);

export default Profile;
